import { PiazzaInputContext } from 'ComponentLibrary/src/molecules/Form/PiazzaInputContext'
import { Dropdown as PiazzaDropdown } from 'ComponentLibrary/src/piazza/forms'
import { Mandator } from 'ComponentLibrary/src/molecules/MandatorList/types'
import { MandatorForm, type MandatorFormLabels } from 'ComponentLibrary/src/molecules/MandatorList/MandatorForm'
import { ReactNode, useContext, useEffect, useState } from 'react'
import { Select } from 'ComponentLibrary/src/atoms/Select'
import { Box, FormHelperText, Stack, useTheme } from '@mui/material'
import { TextWithEllipsis } from 'ComponentLibrary/src/molecules/TextWithEllipsis'
import { joinDefinedStrings } from 'ComponentLibrary/src/common/strings'
import { BuildingIcon, PlusCircleIcon } from '@phosphor-icons/react'
import { Modal, tokens } from 'ComponentLibrary'

type MandatorSelectProps = {
    defaultValue: Mandator | 'new' | ''
    error?: boolean
    options: Mandator[]
    selectMandator: (mandator: Mandator) => void
    setIsMandatorSelectOpen?: (isOpen: boolean) => void
    setIsCreatingNewMandator?: (isCreatingNewMandator: boolean) => void
    disabled?: boolean
    copy: MandatorSelectCopy
}

export type MandatorSelectCopy = {
    inputLabel: string
    addNewLabel: string
    addModalTitle: string
    requiredField: string
    formLabels: MandatorFormLabels
}

export function MandatorSelect({
    options,
    error,
    selectMandator,
    defaultValue,
    setIsMandatorSelectOpen,
    setIsCreatingNewMandator,
    disabled,
    copy,
}: MandatorSelectProps) {
    const theme = useTheme()
    const usePiazzaInputs = useContext(PiazzaInputContext)
    const stringifyValue = (value?: Mandator | string) => (typeof value === 'string' ? value : JSON.stringify(value))

    const [isAddingNewMandator, setIsAddingNewMandator] = useState(defaultValue === 'new')
    const [value, setValue] = useState<string>(stringifyValue(defaultValue))

    // we can let the parent know that the form for adding a new mandator is open, without giving them control
    useEffect(() => {
        setIsCreatingNewMandator?.(isAddingNewMandator)
    }, [setIsCreatingNewMandator, isAddingNewMandator])

    const closeSelect = () => setIsMandatorSelectOpen?.(false)

    const handleChange = (option: string) => {
        if (option === 'new') {
            setValue('new')
            setIsAddingNewMandator(true)
        } else {
            const mandator = JSON.parse(option)
            setIsAddingNewMandator(false)
            setValue(option)
            selectMandator(mandator)
            closeSelect()
        }
    }

    const handleFormCancel = () => {
        setValue(stringifyValue(defaultValue))
        setIsAddingNewMandator(false)
        closeSelect()
    }

    const handleFormSubmit = (mandator: Mandator) => {
        selectMandator(mandator)
        setIsAddingNewMandator(false)
        closeSelect()
    }

    return (
        <Stack direction="column" gap={theme.spacing(6)}>
            <Stack direction="column" alignItems="flex-end">
                {usePiazzaInputs ? (
                    <PiazzaDropdown
                        label={copy.inputLabel}
                        disabled={disabled}
                        value={value || undefined}
                        errorMessage={error ? copy.requiredField : undefined}
                        options={[
                            { label: copy.addNewLabel, value: 'new' },
                            ...options.map((option) => ({
                                label: joinDefinedStrings([option.name, option.city], ' - '),
                                value: JSON.stringify(option),
                            })),
                        ]}
                        onValueChange={(value) => {
                            if (value !== undefined) {
                                handleChange(value)
                            }
                        }}
                    />
                ) : (
                    <Select
                        fullWidth
                        inputLabel={copy.inputLabel}
                        error={error}
                        value={value}
                        disabled={disabled}
                        options={[
                            {
                                label: <TextWithIcon icon={<PlusCircleIcon size={tokens.SizingIconM} />} text={copy.addNewLabel} />,
                                value: 'new',
                            },
                        ].concat(
                            options.map((mandatorOption) => ({
                                label: (
                                    <TextWithIcon
                                        icon={<BuildingIcon size={tokens.SizingIconM} />}
                                        text={joinDefinedStrings([mandatorOption.name, mandatorOption.city], ' - ')}
                                    />
                                ),
                                value: JSON.stringify(mandatorOption),
                            }))
                        )}
                        selectProps={{ variant: 'outlined' }}
                        onChange={(e) => handleChange(e.target.value)}
                    />
                )}
                {error && !usePiazzaInputs && (
                    <FormHelperText style={{ margin: '0 2px 0 0', fontSize: '0.5rem' }} error>
                        {copy.requiredField}
                    </FormHelperText>
                )}
            </Stack>
            {isAddingNewMandator && (
                <Modal isOpen={isAddingNewMandator} onClose={handleFormCancel} title={copy.addModalTitle}>
                    <MandatorForm onCancelCallback={handleFormCancel} onSubmitCallback={handleFormSubmit} labels={copy.formLabels} />
                </Modal>
            )}
        </Stack>
    )
}

const TextWithIcon = ({ text, icon }: { text: string; icon: ReactNode }) => {
    const theme = useTheme()
    return (
        <Stack direction="row" spacing={theme.spacing(4)} alignItems="center">
            <Box display="flex" height="100%" color={theme.palette.customtext.black54}>
                {icon}
            </Box>
            <TextWithEllipsis tooltipText={text} typographyProps={{ variant: 'bodyTextLarge', color: theme.palette.customtext.black100 }}>
                {text}
            </TextWithEllipsis>
        </Stack>
    )
}
